import Hammer from 'hammerjs';
import { EmitterEvent } from '../emitter.service';
import { AbstractCanvasDecorator, CanvasCoordinates, DEFAULT_CANVAS_CURSOR, screenToCanvas, ZOOM_DELTA } from './abstract-canvas-decorator';

export function getEventCoordinates(e: Event): CanvasCoordinates {
  let coords: { x: number; y: number };
  if (window.TouchEvent && e instanceof TouchEvent) {
    if (e.touches.length) {
      coords = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.changedTouches.length) {
      coords = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    }
  } else if (e instanceof MouseEvent) {
    coords = { x: e.clientX || e.pageX, y: e.clientY || e.pageY };
  }

  return coords;
}

export class CanvasMouseHandler {
  private readonly abortController: AbortController = new AbortController();
  lHammertime: HammerManager;

  private pinchInitScale: number;
  private isMouseDown: boolean;
  private isDragging: boolean;
  private blockMouseClick: boolean;
  private lastDragPos: CanvasCoordinates = { x: 0, y: 0 };
  private isPinching = false;
  private pinchEndTime = 0;

  constructor(
    private readonly decorator: AbstractCanvasDecorator,
    private readonly canvas: HTMLCanvasElement,
    private readonly debugInfos = false,
    private readonly onlyClickable = false,
  ) {
    this.canvas.style.cursor = DEFAULT_CANVAS_CURSOR;
    this.registerEvents();
  }

  private registerEvents(): void {
    const { signal } = this.abortController;
    this.canvas.addEventListener('click', (e: MouseEvent) => this.mouseClicked(e), { signal });
    this.canvas.addEventListener('mousemove', (e: MouseEvent) => this.mouseMove(e), { signal });

    if (!this.onlyClickable) {
      this.canvas.addEventListener('mousedown', (e: MouseEvent) => this.mouseDown(e), { signal });
      this.canvas.addEventListener('mouseup', (e: MouseEvent) => this.mouseUp(e), { signal, capture: true });
      this.canvas.addEventListener('mouseout', (e: MouseEvent) => this.mouseOut(e), { signal });
      this.canvas.addEventListener('touchstart', (e: TouchEvent) => this.touchStart(e), { signal });
      this.canvas.addEventListener('touchmove', (e: TouchEvent) => this.touchMove(e), { signal });
      this.canvas.addEventListener('touchend', (e: TouchEvent) => this.touchEnd(e), { signal });
      this.canvas.addEventListener('wheel', (e: WheelEvent) => this.mouseWheel(e), { signal });

      this.lHammertime = new Hammer(this.canvas);
      this.lHammertime.add([new Hammer.Pinch()]);
      this.lHammertime.on('pinchstart', () => {
        this.isPinching = true;
        this.pinchInitScale = this.decorator.currentZoom().renderingCtx.sx;
      });
      this.lHammertime.on('pinchend', () => this.pinchEnded());
      this.lHammertime.on('pinchin pinchout', (evt) => this.pinchInOut(evt));
    }
  }

  private resetFlags(): void {
    this.isMouseDown = false;
    this.isDragging = false;
    this.decorator.animating = false;
  }

  private mouseUp(e: MouseEvent | TouchEvent): void {
    this.blockMouseClick = this.isDragging;
    this.resetFlags();

    const eventCoords = getEventCoordinates(e);
    const onCanvasCoords = screenToCanvas(this.canvas, eventCoords, this.decorator.currentZoom().renderingCtx);
    this.decorator.mouseReleased.next({ event: 'mouseUp', value: onCanvasCoords, extras: { eventCoords } });
  }

  private mouseOut(e: MouseEvent): void {
    this.resetFlags();

    const eventCoords = getEventCoordinates(e);
    const onCanvasCoords = screenToCanvas(this.canvas, eventCoords, this.decorator.currentZoom().renderingCtx);
    this.decorator.mouseReleased.next({ event: 'mouseOut', value: onCanvasCoords, extras: { eventCoords } });
  }

  private mouseMove(e: MouseEvent): void {
    const eventCoords = getEventCoordinates(e);
    this.isDragging = this.isMouseDown;
    this.decorator.animating = this.isDragging;
    const onCanvasCoords = screenToCanvas(this.canvas, eventCoords, this.decorator.currentZoom().renderingCtx);

    if (this.isDragging) {
      this.decorator.pan(eventCoords.x - this.lastDragPos.x, eventCoords.y - this.lastDragPos.y);

      this.lastDragPos.x = eventCoords.x;
      this.lastDragPos.y = eventCoords.y;

      this.decorator.mouseDragged.next({ event: 'mouseMove', value: onCanvasCoords, extras: { eventCoords } });
    } else {
      this.canvas.style.cursor = this.decorator.getCursor(eventCoords);

      this.decorator.mouseMoved.next({ event: 'mouseMove', value: onCanvasCoords, extras: { eventCoords } });
    }
  }

  private mouseDown(e: MouseEvent): void {
    this.isMouseDown = true;
    const eventCoords = getEventCoordinates(e);
    this.lastDragPos.x = eventCoords.x;
    this.lastDragPos.y = eventCoords.y;
  }

  private mouseClicked(e: MouseEvent | TouchEvent): void {
    // Block clicks if currently pinching or if pinch ended recently (within 300ms)
    const recentlyPinched = this.pinchEndTime > 0 && Date.now() - this.pinchEndTime < 300;
    if (!this.blockMouseClick && !this.isPinching && !recentlyPinched) {
      const eventCoordinates = getEventCoordinates(e);
      const target = this.decorator.searchPossibleTargets(eventCoordinates);
      if (target) {
        this.decorator.selectTarget(target, eventCoordinates);
      }
    }
  }

  private mouseWheel(e: WheelEvent): void {
    const delta = e.deltaY;
    const zoomState = this.decorator.currentZoom();

    if ((delta < 0 || zoomState.canZoomOut) && (delta > 0 || zoomState.canZoomIn)) {
      const eventCoordinates = getEventCoordinates(e);
      this.decorator.zoom(eventCoordinates, delta);
    }
  }

  private pinchEnded(): void {
    this.isPinching = false;
    this.pinchEndTime = Date.now();
    this.decorator.zoomChanged.next({ event: 'pinchEnd', value: this.decorator.currentZoom() } as EmitterEvent);
  }

  private pinchInOut(event: HammerInput): void {
    if (!this.pinchInitScale) {
      this.pinchInitScale = this.decorator.currentZoom().renderingCtx.sx;
    }

    const zoomState = this.decorator.currentZoom();
    if (zoomState.canZoomOut || zoomState.canZoomIn) {
      const delta = event.scale > 1 ? -ZOOM_DELTA * event.scale : ZOOM_DELTA / event.scale;
      this.decorator.zoom(event.center, delta);
    }
  }

  private touchEnd(e: TouchEvent): void {
    if (!this.isDragging) {
      this.mouseClicked(e);
    } else {
      this.mouseUp(e);
    }

    this.resetFlags();
  }

  private touchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      this.isMouseDown = true;
      const eventCoords = getEventCoordinates(e);
      this.lastDragPos.x = eventCoords.x;
      this.lastDragPos.y = eventCoords.y;
    } else {
      this.isMouseDown = false;
    }
  }

  private touchMove(e: TouchEvent): void {
    e.preventDefault();

    const eventCoords = getEventCoordinates(e);
    this.isDragging = this.isMouseDown;
    this.decorator.animating = this.isDragging;
    const onCanvasCoords = screenToCanvas(this.canvas, eventCoords, this.decorator.currentZoom().renderingCtx);

    if (this.isDragging) {
      this.decorator.pan(eventCoords.x - this.lastDragPos.x, eventCoords.y - this.lastDragPos.y);

      this.lastDragPos.x = eventCoords.x;
      this.lastDragPos.y = eventCoords.y;
      this.decorator.mouseDragged.next({ event: 'touchMove', value: onCanvasCoords, extras: { eventCoords } });
    }

    if (this.debugInfos) {
      this.decorator.mouseMoved.next({ event: 'touchMove', value: onCanvasCoords, extras: { eventCoords } });
    }
  }

  destroy(): void {
    this.abortController.abort();
    this.lHammertime?.destroy();
  }
}
